import { Task, Timestamp } from '@griply/types';
import { filterGoalsByFilterOptions, getWeekStartsOn } from '@griply/utils';
import { endOfDay, startOfDay } from 'date-fns';
import { useExternalCalendarFullCalendarEvents } from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useFilterDocumentFilters } from 'features/tasks';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import {
  getGoalsDeadlineEvents,
  getTasksEventsForDateRange,
} from 'shared/lib/full-calendar';

import { useTodayFilterDocument } from './use-today-filter-document';

export const useDayEvents = (day: Timestamp, tasks: Task[]) => {
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const goals = useGoals();
  const today = useToday();
  const externalFullCalendarEvents = useExternalCalendarFullCalendarEvents(
    day,
    0,
  );

  const taskEvents = useMemo(
    () =>
      getTasksEventsForDateRange(
        user?.settings?.calendar?.hideHabits
          ? tasks.filter(({ schedules }) => !schedules?.length)
          : tasks,
        {
          startDate: startOfDay(day),
          endDate: endOfDay(day),
          today,
          weekStartsOn,
          hideCompleted: user?.settings?.calendar?.hideCompletedTasks,
        },
      ),
    [
      user?.settings?.calendar?.hideHabits,
      user?.settings?.calendar?.hideCompletedTasks,
      tasks,
      day,
      today,
      weekStartsOn,
    ],
  );

  // The day grid supplies its own date, so the `or` date branches are dropped here too.
  const todayFilterDocument = useTodayFilterDocument();
  const filterDocument = useMemo(
    () => ({ ...todayFilterDocument, or: null }),
    [todayFilterDocument],
  );
  const filters = useFilterDocumentFilters(filterDocument, {});

  const filteredGoals = useMemo(
    () => filterGoalsByFilterOptions(goals, filters),
    [goals, filters],
  );

  const goalEvents = useMemo(
    () => getGoalsDeadlineEvents(filteredGoals, weekStartsOn),
    [filteredGoals, weekStartsOn],
  );

  const events = useMemo(
    () => [
      ...(user?.settings?.calendar?.hideExternalCalendarEvents
        ? []
        : externalFullCalendarEvents),
      ...taskEvents,
      ...goalEvents,
    ],
    [
      externalFullCalendarEvents,
      goalEvents,
      taskEvents,
      user?.settings?.calendar?.hideExternalCalendarEvents,
    ],
  );

  return {
    events,
    tasks,
  };
};
